// CompleteProfileModal (player) — soft-gate for the Phase D2 identity rollout.
//
// ════════════════════════════════════════════════════════════════════
// WHY THIS EXISTS (Phase D6 — player parity)
// ════════════════════════════════════════════════════════════════════
//
// Migration 091 added/normalised first_name + last_name + birthdate on
// the users table. New signups MUST provide all three (enforced by
// the registerSchema in auth.controller.js). Existing players who
// signed up before this deploy may have NULL values.
//
// This modal nudges them. It opens automatically on session boot
// whenever any of the three fields is missing. It is DISMISSABLE —
// players can click "Later" and continue using the app — but it
// re-opens on the next page load (and re-login). There is no hard
// gate; this is a soft nudge per the product directive.
//
// The modal PATCHes /api/me with first_name + last_name + birthdate.
// Server-side updateProfileSchema requires min-2-char names and
// YYYY-MM-DD birthdate when present.

import { FormEvent, useEffect, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { useAuth } from '@baaligames/shared-ui/lib/auth';

type Tone = 'ok' | 'err';

export default function CompleteProfileModal() {
  const { user, refreshMe } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ tone: Tone; msg: string } | null>(null);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name ?? '');
      setLastName(user.last_name  ?? '');
      setBirthdate(user.birthdate ?? '');
    }
  }, [user?.id, user?.first_name, user?.last_name, user?.birthdate]);

  if (!user) return null;
  if (dismissed) return null;

  const needsName =
    !((user.first_name || '').trim() && (user.last_name || '').trim());
  const needsBirthdate = !((user.birthdate || '').trim());
  if (!needsName && !needsBirthdate) return null;

  const namesValid =
    firstName.trim().length >= 2 &&
    lastName.trim().length  >= 2 &&
    /^\d{4}-\d{2}-\d{2}$/.test(birthdate);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!namesValid || busy) return;
    setBusy(true); setToast(null);
    try {
      await api.patch('/me', {
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
        birthdate,
      });
      await refreshMe();
      setToast({ tone: 'ok', msg: 'Profile saved' });
      setTimeout(() => setDismissed(true), 600);
    } catch (err) {
      setToast({ tone: 'err', msg: (err as ApiError).message || 'Save failed' });
    } finally {
      setBusy(false);
    }
  }

  function onLater() { setDismissed(true); }

  return (
    <div className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm grid place-items-start sm:place-items-center px-3 sm:px-4 py-4 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 p-5 space-y-4 shadow-2xl my-4">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl
                           bg-violet-50 text-violet-700 ring-1 ring-violet-100
                           dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30 shrink-0">
            <Icon name="badge" size={20} filled />
          </span>
          <div className="min-w-0">
            <h2 className="text-[16px] font-semibold text-neutral-900 dark:text-white">
              Complete your profile
            </h2>
            <p className="text-[12px] text-arena-700 dark:text-neon-200/75 mt-0.5 leading-relaxed">
              Every player on BaaliGames now carries a full name and date
              of birth so your agent can identify you correctly when you
              recharge / withdraw. Please add these now — your username
              is only for login and your DOB is private.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">First name *</div>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                     maxLength={80} placeholder="Aarav"
                     className="w-full rounded-xl px-3 py-2 text-[14px]
                                bg-white dark:bg-arena-700/40
                                ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                                text-neutral-900 dark:text-white
                                outline-none focus:ring-2 focus:ring-violet-500/30" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">Last name *</div>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)}
                     maxLength={80} placeholder="Sharma"
                     className="w-full rounded-xl px-3 py-2 text-[14px]
                                bg-white dark:bg-arena-700/40
                                ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                                text-neutral-900 dark:text-white
                                outline-none focus:ring-2 focus:ring-violet-500/30" />
            </label>
          </div>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">Date of birth *</div>
            <input type="date" value={birthdate} onChange={(e) => setBirthdate(e.target.value)}
                   min="1900-01-01" max="2025-12-31"
                   className="w-full max-w-[10rem] sm:max-w-[12rem] rounded-xl px-3 py-2 text-[14px]
                              bg-white dark:bg-arena-700/40
                              ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                              text-neutral-900 dark:text-white
                              outline-none focus:ring-2 focus:ring-violet-500/30" />
            <span className="block mt-1 text-[10.5px] text-arena-600 dark:text-neon-200/65">Private · never shown to peers</span>
          </label>

          {toast && (
            <div className={`rounded-xl px-3 py-2 text-[12px] ring-1 ring-inset ${
              toast.tone === 'ok'
                ? 'bg-emerald-50 ring-emerald-200/60 text-emerald-800 dark:bg-emerald-500/10 dark:ring-emerald-400/30 dark:text-emerald-200'
                : 'bg-rose-50 ring-rose-200/60 text-rose-800 dark:bg-rose-500/10 dark:ring-rose-400/30 dark:text-rose-200'
            }`}>{toast.msg}</div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onLater} disabled={busy}
                    className="px-3 py-1.5 rounded-full text-[12px] font-semibold text-arena-700 dark:text-neon-200/75 hover:bg-arena-100 dark:hover:bg-arena-700/60 disabled:opacity-50">
              Later
            </button>
            <button type="submit" disabled={!namesValid || busy}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-bold
                               bg-violet-600 hover:bg-violet-500 active:bg-violet-700 text-white
                               shadow-[0_2px_10px_rgba(139,92,246,0.4)]
                               disabled:opacity-50 transition active:scale-95">
              <Icon name={busy ? 'progress_activity' : 'save'} size={14} className={busy ? 'animate-spin' : ''} />
              Save profile
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
